import { styled } from "@linaria/react";
import React, { useLayoutEffect, useState } from "react";
import Dropdown, { DropdownProps } from "../DropdownInput";
import { Input, InputProps } from "../Input/Input";
import { EverGreenStyle } from "@utils/Style/everGreen";
import { ITheme } from "src/theme-revamp";

let currentProductInHash = "";

const ErrorMessage = styled.p`
  margin-top: 8px;
  color: #c50000;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
`;

const SwapText = styled.p`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
`;

const SwapLink = styled.span<{ colorTheme?: string }>`
  color: ${(props) => props.colorTheme || "#883E89"};
  text-decoration-line: underline;
`;

const InputLabel = styled.label<InputLabelProps>`
  color: ${(props) => (props.labelColor ? props.labelColor : "#434343")};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.8;
  margin-bottom: 16px;
  span {
    display: flex;
    /* span {
      margin-left: 3px;
    } */
    img:first-child {
      margin-left: 4px;
      padding: 4px;
    }
  }
`;

const InputGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

export interface InputGroupProps {
  id: string;
  name?: string;
  label: string;
  noBluRequired?: boolean;
  inputType: "input" | "dropdown" | "swapbuttonname";
  placeholder?: string;
  errorMessage?: string;
  inputProps?: Omit<InputProps, "id" | "name" | "placeholder">;
  dropdownProps?: Omit<
    DropdownProps,
    | "id"
    | "name"
    | "bindLabel"
    | "bindId"
    | "margin"
    | "onChange"
    | "variant"
    | "placeholder"
  >;
  invalid?: boolean;
  inputPropsForInputBox?: {
    inputMode:
      | "text"
      | "none"
      | "search"
      | "email"
      | "tel"
      | "url"
      | "numeric"
      | "decimal"
      | undefined;
    inputType:
      | "number"
      | "normal"
      | "numeric"
      | "decimal"
      | "alphanumeric"
      | "email"
      | "tel"
      | undefined;
  };
  labelColor?: string;
  theme?: ITheme;
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  pattern?: string;
  onBlurShowError?: boolean;
  readOnly?: boolean;
  readOnlyColor?: string;
  swap?: string;
  swapModalOpen?: (e: any) => void;
  SwapButton?: (e: any) => JSX.Element;
  mobCode?: string;
  textAlign?: "left" | "center" | "right" | undefined;
  swapClassname?: string;
  swapButtonOnClick?: (e: any) => void;
  LabelToolTip?: () => JSX.Element;
}
export interface InputLabelProps {
  labelColor?: string;
}

export const InputGroup = React.forwardRef(
  (
    {
      id,
      name,
      label,
      inputType,
      inputPropsForInputBox,
      theme,
      invalid,
      placeholder,
      errorMessage,
      dropdownProps,
      inputProps,
      onChange,
      onBlur,
      onFocus,
      labelColor,
      pattern,
      onBlurShowError = false,
      noBluRequired = false,
      readOnly,
      readOnlyColor,
      swap,
      swapModalOpen,
      mobCode,
      textAlign,
      SwapButton,
      swapClassname,
      swapButtonOnClick,
      LabelToolTip,
    }: InputGroupProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const currentProduct = globalThis.location.hash.split("/")[1];

    function getCurrentProductInHash() {
      const currentHash = globalThis.location.hash.split("/")[1];
      switch (currentHash) {
        case "evergreen":
          currentProductInHash = EverGreenStyle.colors.primary;
          break;
        default:
          currentProductInHash = "";
      }
    }
    useLayoutEffect(() => {
      getCurrentProductInHash();
    }, []);

    const [isBlur, setBlur] = useState<boolean>(!onBlurShowError);

    const onInputChange = (e: any) => {
      if (onChange) {
        onChange(e);
      }
    };
    const onInputBlur = (e: any) => {
      if (onBlur) {
        onBlur(e);
      }
      if (onBlurShowError) {
        setBlur(true);
      }
    };
    const onInputFocus = (e: any) => {
      if (onFocus) {
        onFocus(e);
      }
      if (onBlurShowError) {
        setBlur(false);
      }
    };

    return (
      <InputGroupContainer>
        <InputLabel labelColor={labelColor} htmlFor={id}>
          <span>
            {label}
            {LabelToolTip && <LabelToolTip />}
          </span>
        </InputLabel>
        {inputType === "input" && (
          <Input
            inputMode={inputPropsForInputBox?.inputMode}
            typeInput={inputPropsForInputBox?.inputType}
            ref={ref}
            id={id}
            name={name}
            themes={theme}
            placeholder={placeholder}
            invalid={invalid && isBlur}
            textAlign={textAlign ? textAlign : "center"}
            onFocus={onInputFocus}
            onChange={onInputChange}
            onBlur={onInputBlur}
            pattern={pattern}
            margin="0"
            readOnly={readOnly}
            readOnlyColor={readOnlyColor}
            swap={swap}
            mobCode={mobCode}
            {...inputProps}
          />
        )}
        {inputType === "swapbuttonname" && (
          <SwapButtonContainer className={swapClassname}>
            <Input
              inputMode={inputPropsForInputBox?.inputMode}
              typeInput={inputPropsForInputBox?.inputType}
              ref={ref}
              id={id}
              name={name}
              themes={theme}
              placeholder={placeholder}
              invalid={invalid && isBlur}
              textAlign={textAlign ? textAlign : "center"}
              onFocus={onInputFocus}
              onChange={onInputChange}
              onBlur={onInputBlur}
              pattern={pattern}
              margin="0"
              {...inputProps}
              readOnly={readOnly}
              readOnlyColor={readOnlyColor}
              swap={swap}
              mobCode={mobCode}
            />
            {SwapButton && <SwapButton onClick={swapButtonOnClick} />}
          </SwapButtonContainer>
        )}
        {inputType === "dropdown" && (
          <Dropdown
            placeholder={placeholder}
            name={name}
            {...dropdownProps!}
            theme={theme}
            variant="default"
            bindLabel="label"
            bindId="id"
            margin="0"
            onFocus={onInputFocus}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        )}
        {swap && swapModalOpen && inputType !== "swapbuttonname" && (
          <SwapText>
            {swap === "firstName" && currentProduct !== "evergreen" && (
              <>
                <SwapLink
                  colorTheme={currentProductInHash}
                  onClick={() => swapModalOpen("firstName")}
                >
                  Change your name
                </SwapLink>{" "}
                if it is incorrect.
              </>
            )}
            {swap === "lastName" && currentProduct !== "evergreen" && (
              <>
                <SwapLink
                  colorTheme={currentProductInHash}
                  onClick={() => swapModalOpen("lastName")}
                >
                  Change your name
                </SwapLink>{" "}
                if it is incorrect.
              </>
            )}

            {/* For Evergreen */}
            {swap === "firstName" && currentProduct === "evergreen" && (
              <>
                <SwapLink
                  colorTheme={currentProductInHash}
                  onClick={() => swapModalOpen("firstName")}
                >
                  Change
                </SwapLink>{" "}
                your first/given name if it is incorrect.
              </>
            )}
            {swap === "lastName" && currentProduct === "evergreen" && (
              <>
                <SwapLink
                  colorTheme={currentProductInHash}
                  onClick={() => swapModalOpen("lastName")}
                >
                  Change
                </SwapLink>{" "}
                your last/family name if it is incorrect.
              </>
            )}
          </SwapText>
        )}

        {invalid && errorMessage && isBlur && !noBluRequired && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}
        {invalid && errorMessage && noBluRequired && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}
      </InputGroupContainer>
    );
  }
);

InputGroup.defaultProps = {
  inputType: "input",
};

const SwapButtonContainer = styled.div`
  display: flex;
  & > div {
    width: unset;
  }
`;
